import React from 'react';

import { Pagination } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { SearchField } from '@/design-system/search-field';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';

import { SELECTION_CAP } from '../hooks/useCappedSelection';
import { PAGE_SIZE_OPTIONS } from '../hooks/useListQuery';

export interface ListTab<K extends string> {
  key: K;
  label: string;
  count?: number | null;
}

interface ListShellProps<K extends string> {
  tabs?: ListTab<K>[];
  activeTab?: K;
  onTabChange?: (key: K) => void;
  /** Right side of the tab row (selects, search). */
  filters?: React.ReactNode;
  /** Shown between the toolbar and the table (selection bulk bar). */
  bulkBar?: React.ReactNode;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    /** Page sizes offered (default `PAGE_SIZE_OPTIONS`). */
    pageSizeOptions?: number[];
  };
  children: React.ReactNode;
}

/** Card with status tabs + filters on one hairline row, table, pagination. */
export function ListShell<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  filters,
  bulkBar,
  pagination,
  children,
}: ListShellProps<K>) {
  return (
    <Card padding="none">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
        {tabs && tabs.length > 0 ? (
          <Tabs value={activeTab} onValueChange={(v) => onTabChange?.(v as K)}>
            <TabsList
              variant="line"
              className="h-auto min-h-10 w-auto flex-wrap border-0"
            >
              {tabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                  {t.count !== undefined && t.count !== null && (
                    <TabsCount>{t.count}</TabsCount>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : (
          <span />
        )}
        {filters && (
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2 lg:py-2">
            {filters}
          </div>
        )}
      </div>
      {bulkBar}
      <div className="p-4">
        {children}
        {pagination && pagination.total > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onLimitChange}
            pageSizeOptions={pagination.pageSizeOptions ?? PAGE_SIZE_OPTIONS}
          />
        )}
      </div>
    </Card>
  );
}

/** Search box bound to a URL-backed search state. */
export const ListSearch: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <SearchField
    size="sm"
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    debounceMs={0}
    className="w-full max-w-xs flex-1"
  />
);

/** Visible notice that a selection was cut at the cap. */
export const SelectionCapNotice: React.FC<{ cap?: number }> = ({
  cap = SELECTION_CAP,
}) => (
  <div className="mx-4 mt-4" data-testid="selection-cap-notice">
    <Alert status="warning" appearance="stroke" size="sm">
      Maksimal {cap} dokumen dipilih sekaligus — sisanya tidak ikut dipilih.
    </Alert>
  </div>
);

/**
 * "n selected" bar with clear + bulk action (selection lists).
 * Not the kit BulkActionBar: that one hard-codes English copy ("selected",
 * "Clear") and grabs focus / Escape on appear.
 */
export const BulkBar: React.FC<{
  count: number;
  noun: string;
  actionLabel: string;
  actionIcon: string;
  busy?: boolean;
  /** The selection hit SELECTION_CAP — say so. */
  capped?: boolean;
  /** A second bulk action (e.g. the other kind of row in a mixed selection). */
  secondary?: { label: string; icon: string; onAction: () => void } | null;
  onClear: () => void;
  onAction: () => void;
}> = ({
  count,
  noun,
  actionLabel,
  actionIcon,
  busy,
  capped,
  secondary,
  onClear,
  onAction,
}) => {
  if (!count) return null;
  return (
    <>
      {capped && <SelectionCapNotice />}
      <div className="mx-4 mt-4 flex items-center justify-between gap-3 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <span className="text-[13px] text-text-sub-600">
          <span className="font-semibold tabular-nums text-text-strong-950">
            {count}
          </span>{' '}
          {noun}
        </span>
        <div className="flex items-center gap-2">
          <Button tone="neutral" style="ghost" size="xs" onClick={onClear}>
            Batal pilih
          </Button>
          {secondary && (
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={secondary.onAction}
              disabled={busy}
              leftIcon={<i className={`${secondary.icon} text-[14px]`} />}
            >
              {secondary.label}
            </Button>
          )}
          <Button
            size="xs"
            onClick={onAction}
            loading={busy}
            leftIcon={<i className={`${actionIcon} text-[14px]`} />}
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    </>
  );
};

/** Empty-table message (kit EmptyState) for the `emptyState` slot of TableData. */
export const EmptyTable: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <EmptyState size="sm" className="py-0">
    <EmptyStateIcon className="mb-0 text-icon-soft-400">
      <i className="ri-inbox-line text-[20px]" aria-hidden />
    </EmptyStateIcon>
    <EmptyStateTitle>{title}</EmptyStateTitle>
    {description && (
      <EmptyStateDescription>{description}</EmptyStateDescription>
    )}
  </EmptyState>
);
